/**
 * SoonComePage — public "coming soon" landing.
 *
 * Used as the destination for production traffic that would otherwise
 * see /login (and any auth-funnel page) while public signup is dark.
 * Renders regardless of auth state — no AuthProvider dependency.
 *
 * Activation: the `REACT_APP_LOGIN_DISABLED` env var on the production
 * deployment. When set to `'true'`, App.js redirects /login, /signup,
 * /register, /forgot-password, /reset-password, /verify-email here, and
 * ProtectedRoute bounces unauth requests here instead of /login. When
 * unset, this page is still reachable at /sooncome directly but the
 * auth funnel works normally — preview keeps developing.
 *
 * The demo bypass (/demo?key=…) goes through POST /api/demo/login-as-brand
 * and never touches the public auth funnel, so investor walkthroughs
 * remain alive by construction.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Waves, Loader2, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function EarlyAccessForm() {
  const [state, setState] = useState({
    name:     '',
    company:  '',
    email:    '',
    interest: '',
    website:  '',   // honeypot — invisible to real users, populated only by bots
  });
  const [status, setStatus] = useState('idle');   // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const update = (k) => (e) => setState((s) => ({ ...s, [k]: e.target.value }));

  const canSubmit = state.name.trim() && state.company.trim() && state.email.trim() && status !== 'submitting';

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setStatus('submitting');
    setErrorMsg('');
    try {
      await axios.post(`${API}/api/public/early-access-leads`, state);
      setStatus('success');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setErrorMsg(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div
        data-testid="sooncome-success"
        className="rounded-2xl p-6 text-left"
        style={{ background: 'rgba(74,222,128,0.06)', border: '1px solid rgba(74,222,128,0.30)' }}
      >
        <div className="flex items-center gap-2 mb-1">
          <CheckCircle2 className="h-4 w-4" style={{ color: '#4ADE80' }} />
          <p className="text-sm font-black" style={{ color: '#4ADE80' }}>Got it.</p>
        </div>
        <p className="text-[12px] text-gray-400 leading-relaxed">
          We'll be in touch within 48 hours from <span className="text-gray-200 font-bold">hello@wavedsp.ai</span>.
          Keep an eye on your inbox, including spam, just in case.
        </p>
      </div>
    );
  }

  return (
    <form
      data-testid="sooncome-form"
      onSubmit={submit}
      className="space-y-3 text-left"
      autoComplete="on"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input
          type="text"
          value={state.name}
          onChange={update('name')}
          placeholder="Your name"
          autoComplete="name"
          required
          maxLength={120}
          data-testid="sooncome-input-name"
          className="w-full px-4 py-3 rounded-xl bg-[#111114] text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-1 transition-all"
          style={{ border: '1px solid rgba(255,255,255,0.08)' }}
        />
        <input
          type="text"
          value={state.company}
          onChange={update('company')}
          placeholder="Company / brand"
          autoComplete="organization"
          required
          maxLength={120}
          data-testid="sooncome-input-company"
          className="w-full px-4 py-3 rounded-xl bg-[#111114] text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-1 transition-all"
          style={{ border: '1px solid rgba(255,255,255,0.08)' }}
        />
      </div>
      <input
        type="email"
        value={state.email}
        onChange={update('email')}
        placeholder="Work email"
        autoComplete="email"
        required
        data-testid="sooncome-input-email"
        className="w-full px-4 py-3 rounded-xl bg-[#111114] text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-1 transition-all"
        style={{ border: '1px solid rgba(255,255,255,0.08)' }}
      />
      <textarea
        value={state.interest}
        onChange={update('interest')}
        placeholder="What would you use WAVE for? (optional, one sentence is fine)"
        rows={2}
        maxLength={500}
        data-testid="sooncome-input-interest"
        className="w-full px-4 py-3 rounded-xl bg-[#111114] text-white text-[13px] placeholder-gray-500 focus:outline-none focus:ring-1 transition-all resize-none"
        style={{ border: '1px solid rgba(255,255,255,0.08)' }}
      />

      {/* Honeypot — visually + ARIA hidden, bots fill, real users don't.
          Tab-index -1 ensures keyboard users skip it; autocomplete="off"
          stops password managers from filling it; aria-hidden tells
          screen readers to ignore. */}
      <input
        type="text"
        value={state.website}
        onChange={update('website')}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        name="website"
        placeholder="Leave blank"
        style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
      />

      {errorMsg && (
        <p data-testid="sooncome-error" className="text-[11px]" style={{ color: '#FF6B6B' }}>{errorMsg}</p>
      )}

      <button
        type="submit"
        disabled={!canSubmit}
        data-testid="sooncome-submit"
        className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-black text-white transition-all hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          background: '#FF4D94',
          boxShadow: '0 0 22px rgba(255,77,148,0.32)',
        }}
      >
        {status === 'submitting' ? (<><Loader2 className="h-4 w-4 animate-spin" />Sending…</>) : 'Request early access'}
      </button>
      <p className="text-[10px] text-center text-gray-600">
        We only use this to onboard you. No newsletters, no resale.
      </p>
    </form>
  );
}

export default function SoonComePage() {
  return (
    <div
      data-testid="sooncome-page"
      className="min-h-screen w-full flex flex-col items-center justify-center px-6 py-12"
      style={{
        background: 'radial-gradient(circle at 20% 30%, rgba(255,77,148,0.10), transparent 40%), radial-gradient(circle at 80% 70%, rgba(0,212,255,0.08), transparent 50%), #0A0A0A',
      }}
    >
      <div className="max-w-xl w-full text-center space-y-7">
        <Link to="/" className="inline-flex items-center gap-2 text-white hover:opacity-80 transition-opacity">
          <Waves className="h-7 w-7" style={{ color: '#00D4FF' }} />
          <span className="text-2xl font-black tracking-tight">WAVE</span>
        </Link>

        <div className="space-y-3">
          <p
            className="text-[10px] uppercase tracking-[0.3em] font-black"
            style={{ color: '#FF4D94' }}
          >
            Private beta
          </p>
          <h1 className="text-4xl sm:text-5xl font-black text-white leading-tight">
            We're opening WAVE to brands<br />by invitation.
          </h1>
          <p className="text-base text-gray-400 leading-relaxed max-w-md mx-auto">
            Public sign-up is closed while we onboard our first wave of brand
            partners. Drop your details and we'll be in touch.
          </p>
        </div>

        <EarlyAccessForm />

        <Link
          to="/"
          data-testid="sooncome-back-link"
          className="inline-block text-[11px] font-bold text-gray-500 hover:text-white transition-colors"
        >
          ← Back to wavedsp.ai
        </Link>
      </div>
    </div>
  );
}
